import { useAuthStore } from '@/store/authStore';
import type { CurrentUser } from '@/types/auth';
import type { PermissionKey } from '@/types/permissions';

export type PermissionMode = 'all' | 'any';

type PermissionSubject = Pick<CurrentUser, 'permissions' | 'isSuperAdmin'> | null | undefined;

/**
 * Pure permission check. Super admins pass everything. Otherwise the required
 * key(s) must be present in the user's resolved permission set. `mode` decides
 * whether all or any of the keys are required. An empty requirement = allowed.
 *
 * This gates UI affordances only; the backend re-checks every request (spec §36).
 */
export function hasPermission(
  user: PermissionSubject,
  required: PermissionKey | PermissionKey[] | undefined,
  mode: PermissionMode = 'all',
): boolean {
  if (!required || (Array.isArray(required) && required.length === 0)) return true;
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  const granted = new Set(user.permissions);
  const keys = Array.isArray(required) ? required : [required];
  return mode === 'any' ? keys.some((k) => granted.has(k)) : keys.every((k) => granted.has(k));
}

/** Reactive permission check bound to the current user in the auth store. */
export function useHasPermission(
  required: PermissionKey | PermissionKey[] | undefined,
  mode: PermissionMode = 'all',
): boolean {
  const user = useAuthStore((s) => s.user);
  return hasPermission(user, required, mode);
}

/** Bundle of permission helpers for a component that needs several checks. */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  return {
    user,
    isSuperAdmin: user?.isSuperAdmin ?? false,
    can: (required: PermissionKey | PermissionKey[], mode: PermissionMode = 'all') =>
      hasPermission(user, required, mode),
    canAny: (required: PermissionKey[]) => hasPermission(user, required, 'any'),
    hasRole: (slug: string) => user?.roleSlugs.includes(slug) ?? false,
  };
}
